import { useLocation } from 'react-router-dom';
import AddIngredients, { AddIngredientSelection } from './AddIngredients';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customerRequestsService } from '../../../../../services/customerRequests/customerRequestsService';
import { refreshAfterCustomerRequestApproval } from '../../../../../services/customerRequests/refreshAfterApproval';
import type { CustomerAlternativeContext, CustomerAlternativeSaveReceipt, CustomerRequestUnit } from '../../../../../services/customerRequests/types';
import { fetchCostPriceAnalysisById, CostPriceAnalysis, CostPriceIngredient, IngredientAlternative } from '../../../../../services/costPrice/costPriceService';
import { useCostPriceStore } from '../../../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../../../stores/appSubrecipesStore';
import { useCustomerRequestsStore } from '../../../../../stores/appCustomerRequestsStore';
import { useAppUserDefaultStore } from '../../../../../stores/appUserDefaultStore';
import { borderPrimary, outlinedActionButtonClass, primaryText } from '../../../../../styling/stylingHelper';
import { productKey } from '../../../../../utils/reports/shared';
import ApplyAlternativeToDishesPanel from './ApplyAlternativeToDishesPanel';
import { findAlternativeTargets } from './alternativesPropagation';

export default function CustomerAlternativeReview({ context, primary, defaultWastePercentage, onSaved, onBack, onClose, onDone }: {
    context: CustomerAlternativeContext;
    primary: { productName?: string; quantityNeeded?: number; quantityInfo?: { stdUnit: string }; wastePercentage?: number };
    defaultWastePercentage: number;
    onSaved: (ingredient: CostPriceIngredient, analysis: CostPriceAnalysis) => void;
    onBack: () => void; onClose: () => void; onDone: () => void;
}) {
    const { t } = useTranslation();
    const location = useLocation();
    const note = (location.state as { customerAlternativeNote?: string } | null)?.customerAlternativeNote;
    const [refreshError, setRefreshError] = useState(false);
    const [receipt, setReceipt] = useState<CustomerAlternativeSaveReceipt | null>(null);
    const [showPropagation, setShowPropagation] = useState(true);
    const submission = useRef({ key: crypto.randomUUID(), amount: '' });
    const inFlight = useRef(false);
    const costPrices = useCostPriceStore(s => s.costPriceList);
    const subrecipes = useSubrecipesStore(s => s.subrecipesList);
    const primaryKey = productKey(context.primaryProduct.platform, context.primaryProduct.articleId, context.primaryProduct.productName);
    const targets = useMemo(() => findAlternativeTargets(primaryKey, context.analysisId, costPrices, subrecipes), [primaryKey, context.analysisId, costPrices, subrecipes]);
    const savedAlternative: IngredientAlternative | null = receipt ? {
        ...context.candidateProduct, articleId: context.candidateProduct.articleId ?? '', id: receipt.alternativeId, quantityNeeded: receipt.amount.value,
        quantityInfo: { ...context.candidateProduct.quantityInfo, stdUnit: receipt.amount.unit },
        wastePercentage: receipt.wastePercentage,
    } : null;

    const refresh = async (savedReceipt = receipt) => {
        setRefreshError(false);
        try {
            await refreshAfterCustomerRequestApproval({ type: 'propose_alternative', restaurant: context.restaurant, actionResult: savedReceipt?.actionResult ?? null });
            const fresh = await fetchCostPriceAnalysisById(context.analysisId);
            if (useAppUserDefaultStore.getState().restaurant?.id !== context.restaurant.id) return;
            if (fresh.restaurantId !== context.restaurant.id) throw new Error('Refreshed dish has a different restaurant');
            useCostPriceStore.getState().upsertOne(fresh);
            // The receipt is authoritative after a save that regenerates row ids.
            const id = savedReceipt?.ingredientId ?? context.ingredientId;
            const ingredient = fresh.ingredients.find(row => row.id === id);
            if (ingredient) onSaved(ingredient, fresh);
        } catch (failure) {
            console.error('Alternative saved, but refreshing its editor failed:', failure);
            setRefreshError(true);
        }
        void useCustomerRequestsStore.getState().refresh().catch(failure => console.error('Failed to refresh customer questions:', failure));
    };
    const save = async (selection: AddIngredientSelection) => {
        if (selection.kind !== 'product' || inFlight.current || receipt) return;
        if (useAppUserDefaultStore.getState().restaurant?.id !== context.restaurant.id) throw new Error(t('customerRequests.alternativeEditor.contextChanged'));
        inFlight.current = true;
        const amount = selection.quantityNeeded;
        const unit = context.candidateProduct.quantityInfo.stdUnit as CustomerRequestUnit;
        const signature = `${amount}:${unit}:${selection.wastePercentage}:${note ?? ''}`;
        if (submission.current.amount && submission.current.amount !== signature) submission.current.key = crypto.randomUUID();
        submission.current.amount = signature;
        try {
            const result = await customerRequestsService.saveAlternative(context.requestId, context.revision, submission.current.key,
                { value: amount, unit }, selection.wastePercentage, note);
            setReceipt(result);
            await refresh(result);
        } catch (failure) {
            console.error('Failed to save proposed alternative:', failure);
            throw new Error(t('customerRequests.alternativeEditor.saveError'));
        } finally { inFlight.current = false; }
    };

    if (!receipt) return <AddIngredients mode="alternatives" restaurantId={context.restaurant.id}
        alternativePrimary={primary} defaultWastePercentage={defaultWastePercentage}
        initialProduct={{ ...context.candidateProduct, name: context.candidateProduct.productName }} lockProductSelection
        returnAfterAdd={false} onAddIngredient={save} onBack={onBack} onClose={onClose}
        title={t('costPrice.alternatives.addTitle')} addLabel={t('customerRequests.alternativeEditor.save')} />;

    return <section aria-label={t('customerRequests.alternativeEditor.title')} className={`mb-5 space-y-3 rounded-lg border p-4 ${borderPrimary}`}>
        <button type="button" className={outlinedActionButtonClass} onClick={onDone}>{t('common.back')}</button>
        <p role="status" className={primaryText}>{t('customerRequests.alternativeEditor.saved')}</p>
        {refreshError && <div role="alert"><p>{t('customerRequests.alternativeEditor.refreshError')}</p><button type="button" className={outlinedActionButtonClass} onClick={() => void refresh()}>{t('customerRequests.retry')}</button></div>}
        {receipt && savedAlternative && showPropagation && targets.some(target => !target.existingAlternativeKeys.includes(productKey(savedAlternative.platform, savedAlternative.articleId, savedAlternative.productName))) &&
            <ApplyAlternativeToDishesPanel primaryName={context.primary.name} primaryKey={primaryKey} alternative={savedAlternative} sourcePrimaryQuantity={primary.quantityNeeded ?? 0} targets={targets} onDone={() => setShowPropagation(false)} />}
    </section>;
}
